import { DefaultQueryParams } from '@frontend/api-utils';
import { MultiObjectSelectInput, SelectInputV2 } from '@frontend/components/basic-forms';
import { ActionModal } from '@frontend/components/modal';
import { UserGroupClient } from '@frontend/group/api';
import { IGroup } from '@frontend/group/types';
import { CommonMessage } from '@frontend/lang';
import { UserContactClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { FormattedMessage, useIntl } from 'react-intl';

import { DelegateTransactionModalProps, DelegationPartyType, useDelegateTransactionModal } from './delegate-transaction-modal.controller';

type PartyOption = (IUser | IGroup) & { partyType: string };

export const DelegateTransactionModal = (props: DelegateTransactionModalProps) => {
    const viewProps = useDelegateTransactionModal(props);
    const intl = useIntl();

    const usersLabel = intl.formatMessage({ id: 'Objects.USER.PLURAL', defaultMessage: 'Users', description: 'Plural name for users' });
    const userGroupsLabel = intl.formatMessage({ id: 'Objects.USER_GROUP.PLURAL', defaultMessage: 'User groups', description: 'Plural name for user groups' });
    const receiverLabel = intl.formatMessage({ id: 'Objects.TRANSACTION.RECEIVER', defaultMessage: 'Receiver', description: 'Label for receiver' });
    const senderLabel = intl.formatMessage({ id: 'Objects.TRANSACTION.SENDER', defaultMessage: 'Sender', description: 'Label for sender' });
    const isReceiver = viewProps.delegationType === 'receiver';

    return (
        <ActionModal
            title={
                <FormattedMessage
                    id='platform.transaction.delegate-transaction-modal.title'
                    description='Title of the delegate transaction modal'
                    defaultMessage='Delegate transaction'
                />
            }
            cancel={{
                onCancel: viewProps.handleCancel,
                label:
                    viewProps.step === 'confirm' ? (
                        <FormattedMessage
                            id='platform.transaction.delegate-transaction-modal.back'
                            description='Back button label on the delegate transaction confirmation step'
                            defaultMessage='Back'
                        />
                    ) : (
                        CommonMessage.BUTTONS.CANCEL
                    )
            }}
            confirm={{
                onConfirm: viewProps.handlePrimaryAction,
                label: viewProps.step === 'confirm' ? CommonMessage.BUTTONS.CONFIRM : CommonMessage.BUTTONS.CONTINUE,
                disabled: viewProps.step === 'select' && viewProps.newPartyUserIds.length === 0,
                isLoading: viewProps.isSubmitting
            }}>
            {viewProps.step === 'select' ? (
                <div className='flex w-full flex-col gap-4'>
                    <div className='text-brand-muted text-base font-medium'>
                        {isReceiver ? (
                            <FormattedMessage
                                id='platform.transaction.delegate-transaction-modal.explanation'
                                description='Explanation of what delegating a transaction means in the delegate transaction modal'
                                defaultMessage='Delegating will add the selected users or groups as receivers to the transaction. They will potentially be notified with pick-up information.'
                            />
                        ) : (
                            <FormattedMessage
                                id='platform.transaction.delegate-transaction-modal.explanation-senders'
                                description='Explanation of what delegating a transaction to senders means in the delegate transaction modal'
                                defaultMessage='Delegating will add the selected users or groups as senders to the transaction. They will potentially be notified with drop-off information.'
                            />
                        )}
                    </div>
                    <SelectInputV2<DelegationPartyType>
                        label={
                            <FormattedMessage
                                id='platform.transaction.delegate-transaction-modal.party-type-label'
                                description='Label for the sender/receiver type selector in the delegate transaction modal'
                                defaultMessage='Delegate as'
                            />
                        }
                        className='w-full'
                        disableSearch
                        isClearable={false}
                        value={viewProps.delegationType}
                        options={[
                            { value: 'receiver', label: receiverLabel },
                            { value: 'sender', label: senderLabel }
                        ]}
                        onSelect={(option) => {
                            if (option?.value === 'receiver' || option?.value === 'sender') {
                                viewProps.handleDelegationTypeChange(option.value);
                            }
                        }}
                    />
                    <MultiObjectSelectInput<PartyOption, DefaultQueryParams>
                        key={`delegate-transaction-${viewProps.delegationType}-select`}
                        fetch={async (arg) => {
                            const personal = await UserContactClient.getMyContacts({ ...arg, account_id: viewProps.currentAccount });
                            const corporate = await UserContactClient.getCorporateContacts({ ...arg, account_id: viewProps.currentAccount });
                            const userGroups = await UserGroupClient.fetchUserGroups({ ...arg, account_id: viewProps.currentAccount });

                            const usersWithType = [...personal.results, ...corporate.results].map((user) => ({
                                ...user,
                                partyType: usersLabel
                            }));
                            const groupsWithType = userGroups.results.map((group) => ({
                                ...group,
                                partyType: userGroupsLabel
                            }));

                            return {
                                count: personal.count + corporate.count + userGroups.count,
                                results: [...usersWithType, ...groupsWithType] as PartyOption[]
                            };
                        }}
                        mapOptions={'partyType' as keyof PartyOption}
                        itemLabel={(obj) => {
                            if ('first_name' in obj) {
                                return `${obj.first_name ?? ''} ${obj.last_name ?? ''}`.trim();
                            }

                            return obj.name;
                        }}
                        label={isReceiver ? CommonMessage.OBJECTS.TRANSACTION.RECEIVERS : CommonMessage.OBJECTS.TRANSACTION.SENDERS}
                        onChange={(value) => {
                            const users = value.filter((item): item is IUser & { partyType: string } => 'first_name' in item);
                            const groups = value.filter((item): item is IGroup & { partyType: string } => !('first_name' in item));
                            void viewProps.handlePartyChange(users, groups);
                        }}
                        value={[...viewProps.selectedUserIds, ...viewProps.selectedGroupIds]}
                    />
                </div>
            ) : (
                <div className='text-brand-muted text-base font-medium'>
                    {isReceiver ? (
                        <FormattedMessage
                            id='platform.transaction.delegate-transaction-modal.confirmation'
                            description='Confirmation message shown before adding new receivers when delegating a transaction'
                            defaultMessage='{count, plural, one {# user will be added as a receiver.} other {# users will be added as receivers.}} Do you want to continue?'
                            values={{ count: viewProps.newPartyUserIds.length }}
                        />
                    ) : (
                        <FormattedMessage
                            id='platform.transaction.delegate-transaction-modal.confirmation-senders'
                            description='Confirmation message shown before adding new senders when delegating a transaction'
                            defaultMessage='{count, plural, one {# user will be added as a sender.} other {# users will be added as senders.}} Do you want to continue?'
                            values={{ count: viewProps.newPartyUserIds.length }}
                        />
                    )}
                </div>
            )}
        </ActionModal>
    );
};

export type { DelegateTransactionModalProps };
